import axios from "axios";
import React, { useEffect, useState } from "react";
import { Controller, useWatch } from "react-hook-form";

import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Button, CircularProgress, Dialog, Skeleton } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material/utils";
// import parse from "autosuggest-highlight/parse";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useGetCountryCodesQuery } from "../../../features/cargo/cargoApiSlice";
import ControlledSelect from "../ControlledSelect";
import ControlledTextField from "../ControlledTextField";

const BASE_URL = import.meta.env.VITE_APP_API_URL;

const ControlledLocationAutocomplete = ({
	resetKey,
	control,
	defaultValue,
	name,
	label,
	errors,
	setFormValue,
	InputLabelProps,
	inputProps,
	...rest
}) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { data: countryCodes, isLoading: isLoadingCountryCodes } =
		useGetCountryCodesQuery();
	const [value, setValue] = useState(null);
	const [inputValue, setInputValue] = useState("");
	const [options, setOptions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);

	// custom address
	const [open, setOpen] = useState(false);
	const [stopFetch, setStopFetch] = useState(true);
	const handleClose = () => {
		setOpen(false);
	};
	const handleOpen = () => {
		setOpen(true);
	};

	// watch country select
	const countrySelectValue = useWatch({
		control,
		name: `${name}Country`,
		defaultValue: "",
	});

	const fetch = React.useMemo(
		() =>
			debounce((request, callback) => {
				setIsLoading(true);
				axios
					.post(
						`${BASE_URL}/api/geocode/autocomplete`,
						{ country: countrySelectValue, queryString: request.input },
						{
							withCredentials: true,
						},
					)
					.then((response) => {
						callback(response.data.items);
					})
					.catch((_error) => {
						enqueueSnackbar(t("default_error"), {
							variant: "error",
							preventDuplicate: true,
						});
						callback([]);
					})
					.finally(() => {
						setIsLoading(false);
					});
			}, 1600),
		[countrySelectValue, enqueueSnackbar, t],
	);

	useEffect(() => {
		let active = true;

		if (!stopFetch) {
			if (
				inputValue === "" ||
				inputValue.length < 2 ||
				countrySelectValue === ""
			) {
				setOptions(value ? [value] : []);
				return undefined;
			}

			fetch({ input: inputValue }, (results) => {
				if (active) {
					let newOptions = [];

					if (value) {
						newOptions = [value];
					}

					if (results && Array.isArray(results)) {
						newOptions = [...newOptions, ...results];
					}

					if (newOptions.length < 1) {
						setOpen(true);
					}

					setOptions([...newOptions, "DIALOG"]);
				}
			});
		}

		return () => {
			active = false;
		};
	}, [value, inputValue, countrySelectValue]);

	const isOptionEqualToValue = (option, value) => {
		if (typeof option === "string" || typeof value === "string") {
			return option === value;
		}

		return option?.title === value?.title;
	};

	useEffect(() => {
		setValue(null);
		setInputValue("");
		setOptions([]);
	}, [resetKey]);

	useEffect(() => {
		if (defaultValue) {
			setInputValue(
				// `${defaultValue.address.countryCode} ${defaultValue.address.postalCode} ${defaultValue.address.city}`
				`${defaultValue.address.postalCode} ${defaultValue.address.city}`,
			);
		}
	}, [defaultValue]);

	// watch self value
	const autocompleteValue = useWatch({
		control,
		name: name,
		defaultValue: "",
	});

	useEffect(() => {
		if (autocompleteValue) {
			if (autocompleteValue?.address) {
				setInputValue(
					`${autocompleteValue.address.postalCode} ${autocompleteValue.address.city}`,
				);
			} else {
				setInputValue(autocompleteValue);
			}
		}
	}, [autocompleteValue]);

	useEffect(() => {
		if (
			defaultValue?.address?.postalCode &&
			defaultValue?.address?.city &&
			!defaultValue?.selectFirst
		) {
			setStopFetch(true);
		} else {
			setStopFetch(false);
		}
	}, [defaultValue]);

	// select first option - used for AI
	useEffect(() => {
		if (
			!autocompleteValue &&
			defaultValue?.selectFirst &&
			options.length > 0 &&
			options[0] !== "DIALOG"
		) {
			setFormValue(name, options[0], {
				shouldValidate: true,
			});

			setValue(options[0]);
			if (options[0]?.address) {
				setInputValue(
					`${options[0].address.postalCode} ${options[0].address.city}`,
				);
			} else {
				setInputValue(options[0]);
			}

			// clear custom address as HERE address was selected
			setFormValue(`${name}CustomPostalCode`, "", {
				shouldValidate: true,
			});
			setFormValue(`${name}CustomCity`, "", {
				shouldValidate: true,
			});
			setFormValue(`${name}CustomCountry`, "", {
				shouldValidate: true,
			});

			enqueueSnackbar(
				t("postal_code_and_city_found_in_here", {
					value:
						name === "loadingAddress" ? t("load_value") : t("unload_value"),
				}),
				{ variant: "success" },
			);
		}
	}, [defaultValue, options]);

	return (
		<>
			<Grid
				container
				rowSpacing={0}
				columnSpacing={1}
				sx={{ p: 0, width: "100%" }}
			>
				<Grid size={3}>
					{isLoadingCountryCodes ? (
						<Box sx={{ mb: "8px", mt: "16px" }}>
							<Skeleton
								width={65.26}
								height={36.71}
								sx={{
									transform: "scale(1)",
								}}
							/>
						</Box>
					) : (
						<Box sx={{ mb: "8px", mt: "16px" }}>
							<ControlledSelect
								margin="none"
								control={control}
								name={`${name}Country`}
								label={t("country")}
								defaultValue=""
								options={countryCodes.map((code) => {
									return { value: code, label: code };
								})}
								sx={{
									"& .MuiSelect-select": {
										textOverflow: "clip !important",
									},
								}}
								error={!!errors[`${name}Country`] || false}
							/>
						</Box>
					)}
				</Grid>
				<Grid size={9}>
					<Controller
						control={control}
						name={name}
						render={({ field }) => {
							return (
								<Autocomplete
									className="sb-location-autocomplete"
									disabled={!countrySelectValue}
									isOptionEqualToValue={isOptionEqualToValue}
									noOptionsText={
										<Button
											onClick={handleOpen}
											variant="text"
											sx={{
												width: "100%",
												padding: "12px 0px",
												margin: 0,
												textDecoration: "underline",
												"&:hover": {
													textDecoration: "underline",
												},
											}}
										>
											{t("address_autocomplete_no_results")}
										</Button>
									}
									fullWidth
									options={options}
									value={inputValue}
									getOptionLabel={(_option) => {
										return inputValue;
										// return typeof option === "string"
										// 	? option
										// 	: `${option.address.countryCode} ${option.address.postalCode} ${option.address.city}`;
									}}
									renderOption={(props, option) => {
										if (option === "DIALOG") {
											return (
												<li
													{...props}
													key="custom-address-opt"
													style={{ width: "100%" }}
												>
													<Button
														onClick={handleOpen}
														variant="contained"
														sx={{
															width: "100%",
															padding: "12px 0px",
															borderRadius: 0,
														}}
													>
														{t("enter_address_manually")}
													</Button>
												</li>
											);
										}

										// const matches = option.highlights.address.label || [];
										// const parts = parse(
										// 	option.address.label,
										// 	matches.map((match) => [match.start, match.end])
										// );
										// const matches = option.highlights.address.postalCode || [];
										// const parts = parse(
										// 	option.address.postalCode,
										// 	matches.map((match) => [match.start, match.end])
										// );

										return (
											<li
												{...props}
												key={`${option.address.countryCode}-${option.address.postalCode}-${option.address.city}-${props.id}`}
											>
												<Grid
													container
													sx={{
														alignItems: "center",
													}}
												>
													<Grid sx={{ display: "flex", width: 44 }}>
														<LocationOnIcon sx={{ color: "text.secondary" }} />
													</Grid>
													<Grid
														sx={{
															width: "calc(100% - 44px)",
															wordWrap: "break-word",
														}}
													>
														{/* {`${option.address.countryCode} ${option.address.postalCode} ${option.address.city}`} */}
														{`${option.address.postalCode} ${option.address.city}`}
														{/* <Typography variant="body2" color="text.secondary">
															{parts.map((part, index) => {
																return (
																	<Box
																		key={index}
																		component="span"
																		sx={{
																			fontWeight: part.highlight
																				? "bold"
																				: "regular",
																		}}
																	>
																		{part.text}
																	</Box>
																);
															})}
														</Typography> */}
													</Grid>
												</Grid>
											</li>
										);
									}}
									renderInput={(params) => {
										return (
											<TextField
												{...params}
												name={name}
												size="small"
												variant="outlined"
												required
												fullWidth
												label={t("postal_code_and_city")}
												value={inputValue}
												{...rest}
												error={!!errors[name] || false}
												slotProps={{
													...params.slotProps,

													input: {
														...params.slotProps.input,
														endAdornment: (
															<React.Fragment>
																{isLoading ? (
																	<CircularProgress
																		color="inherit"
																		size={20}
																		// sx={{ position: "absolute", right: 0 }}
																	/>
																) : (
																	<></>
																)}
																{params.slotProps.input.endAdornment}
															</React.Fragment>
														),
													},

													inputLabel: { shrink: true, ...InputLabelProps },
													htmlInput: {
														...params.slotProps.htmlInput,
														...inputProps,
													},
												}}
											/>
										);
									}}
									onChange={(event, newValue, reason) => {
										setStopFetch(false);
										setOptions(newValue ? [newValue, ...options] : options);
										setValue(newValue);
										setInputValue(
											// `${newValue.address.countryCode} ${newValue.address.postalCode} ${newValue.address.city}`
											newValue
												? `${newValue.address.postalCode} ${newValue.address.city}`
												: "",
										);
										field.onChange(newValue);

										// clear custom address fields
										if (reason === "clear") {
											setFormValue(`${name}CustomPostalCode`, "", {
												shouldValidate: true,
											});
											setFormValue(`${name}CustomCity`, "", {
												shouldValidate: true,
											});
											setFormValue(`${name}CustomCountry`, "", {
												shouldValidate: true,
											});
										}
									}}
									onInputChange={(event, newInputValue) => {
										const prevInputValue = inputValue;

										if (prevInputValue !== newInputValue) {
											setStopFetch(false);
										}
										if (newInputValue.length < 4) {
											setStopFetch(false);
										}
										setInputValue(newInputValue);
									}}
								/>
							);
						}}
					/>
				</Grid>
			</Grid>
			<CustomAddressDialog
				onClose={handleClose}
				open={open}
				control={control}
				name={name}
				setInputValue={setInputValue}
				setStopFetch={setStopFetch}
				setValue={setFormValue}
				countryCodes={countryCodes}
				isLoadingCountryCodes={isLoadingCountryCodes}
			/>
		</>
	);
};

export default ControlledLocationAutocomplete;

const CustomAddressDialog = ({
	onClose,
	open,
	control,
	name,
	setInputValue,
	setStopFetch,
	setValue,
	countryCodes,
	isLoadingCountryCodes,
}) => {
	const { t } = useTranslation();
	const [errors, setErrors] = useState({
		customPostalCode: false,
		customCity: false,
		customCountry: false,
	});
	const { enqueueSnackbar } = useSnackbar();

	const customPostalCode = useWatch({
		control,
		name: `${name}CustomPostalCode`,
		defaultValue: "",
	});

	const customCity = useWatch({
		control,
		name: `${name}CustomCity`,
		defaultValue: "",
	});

	const customCountry = useWatch({
		control,
		name: `${name}CustomCountry`,
		defaultValue: "",
	});

	useEffect(() => {
		setValue(`${name}CustomPostalCode`, "");
		setValue(`${name}CustomCity`, "");
		setValue(`${name}CustomCountry`, "");
	}, []);

	const handleSave = () => {
		if (!customPostalCode) {
			setErrors((prev) => ({ ...prev, customPostalCode: true }));
		}

		if (!customCity) {
			setErrors((prev) => ({ ...prev, customCity: true }));
		}

		if (!customCountry) {
			setErrors((prev) => ({ ...prev, customCountry: true }));
		}

		if (!customPostalCode || !customCity || !customCountry) {
			enqueueSnackbar(t("fill_required_fields"), {
				variant: "error",
				preventDuplicate: true,
			});
			return;
		}
		setValue(`${name}Country`, customCountry);
		setInputValue(`${customPostalCode}, ${customCity}`);
		setStopFetch(true);
		onClose();
	};

	return (
		<Dialog
			fullWidth
			maxWidth="xs"
			onClose={onClose}
			open={open}
			key={control.id}
		>
			<Box sx={{ padding: "30px" }}>
				<Box sx={{ textAlign: "center" }}>
					<Typography
						component="h3"
						variant="h5"
						sx={{ marginTop: 0, fontWeight: 600 }}
					>
						{t("address_not_found_add_manually")}
					</Typography>
				</Box>
				<Grid container rowSpacing={0} columnSpacing={1} sx={{ py: 1, px: 2 }}>
					<Grid size={8}>
						<ControlledTextField
							control={control}
							margin="normal"
							name={`${name}CustomPostalCode`}
							defaultValue=""
							error={!!errors?.customPostalCode}
							label={t("postal_code_label") + "*"}
							sx={{ mb: 0 }}
						/>
					</Grid>
					<Grid size={4}>
						<Box
							sx={{
								display: "flex",
								alignItems: "end",
								height: "100%",
							}}
						>
							{isLoadingCountryCodes ? (
								<div>
									<Skeleton
										width={112}
										height={36.71}
										sx={{
											transform: "scale(1)",
										}}
									/>
								</div>
							) : (
								<ControlledSelect
									margin="none"
									control={control}
									name={`${name}CustomCountry`}
									label={t("country") + "*"}
									defaultValue=""
									error={!!errors?.customCountry}
									options={countryCodes.map((code) => {
										return { value: code, label: code };
									})}
									sx={{ mb: 0 }}
								/>
							)}
						</Box>
					</Grid>
					<Grid size={12}>
						<ControlledTextField
							control={control}
							margin="normal"
							name={`${name}CustomCity`}
							defaultValue=""
							error={!!errors?.customCity}
							label={t("city_label") + "*"}
							sx={{ mb: 0 }}
						/>
					</Grid>
				</Grid>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						mt: 2,
					}}
				>
					<Button onClick={handleSave} variant="contained">
						{t("save")}
					</Button>
				</Box>
			</Box>
		</Dialog>
	);
};
